import React, { useEffect, useState } from "react";
import {
   Table,
   Button,
   Popconfirm,
   message,
   Modal,
   Form,
   Input,
   DatePicker,
   Select,
   Row,
   Col,
   Tag,
   Tooltip
} from "antd";

import {
   EditOutlined,
   DeleteOutlined,
   PlusOutlined
} from "@ant-design/icons";

import {
   getDailyVisits,
   deleteDailyVisit,
   updateDailyVisit,
   createDailyVisit,
   exportDailyVisits
} from "../../services/dailyVisitService";

import dayjs from "dayjs";

const DailyVisits = () => {
   const [visits, setVisits] = useState([]);
   const [isModalOpen, setIsModalOpen] = useState(false);
   const [editingVisit, setEditingVisit] = useState(null);

   const [isAddModalOpen, setIsAddModalOpen] = useState(false);

   const [search, setSearch] = useState("");
   const [status, setStatus] = useState("");
   const [dateRange, setDateRange] = useState([]);

   const [form] = Form.useForm();
   const [addForm] = Form.useForm();


   const fetchVisits = async () => {

      const filters = {};

      if (search) filters.search = search;
      if (status) filters.status = status;

      if (dateRange.length === 2) {
         filters.start_date = dateRange[0].format("YYYY-MM-DD");
         filters.end_date = dateRange[1].format("YYYY-MM-DD");
      }

      const data = await getDailyVisits(filters);

      setVisits(data);

   };

   useEffect(() => {
      fetchVisits();
   }, [search, status, dateRange]);

   const handleAddVisit = async (values) => {
      try {

         const payload = {
            ...values,
            visit_date: values.visit_date
               ? values.visit_date.format("YYYY-MM-DD")
               : null,
            next_follow_date: values.next_follow_date
               ? values.next_follow_date.format("YYYY-MM-DD")
               : null
         };

         await createDailyVisit(payload);

         message.success("Visit added successfully");

         addForm.resetFields();
         setIsAddModalOpen(false);

         fetchVisits();

      } catch (err) {
         message.error(
            err?.response?.data?.message || "Failed to add visit"
         );
      }
   };

   const handleEdit = (record) => {

      setEditingVisit(record);

      form.setFieldsValue({
         client_name: record.client_name,
         phone: record.phone,
         location: record.location,
         note: record.note,
         status: record.status,
         visit_date: record.visit_date ? dayjs(record.visit_date) : null,
         next_follow_date: record.next_follow_date
            ? dayjs(record.next_follow_date)
            : null
      });

      setIsModalOpen(true);

   };

   const handleUpdate = async () => {

      try {

         const values = await form.validateFields();

         await updateDailyVisit(editingVisit.id, {
            ...values,
            visit_date: values.visit_date
               ? values.visit_date.format("YYYY-MM-DD")
               : null,
            next_follow_date: values.next_follow_date
               ? values.next_follow_date.format("YYYY-MM-DD")
               : null
         });

         message.success("Visit updated successfully");

         setIsModalOpen(false);

         fetchVisits();

      } catch (err) {

         message.error(
            err?.response?.data?.message || "Update failed"
         );

      }
   };

   const handleDelete = async (id) => {

      try {

         await deleteDailyVisit(id);

         message.success("Visit deleted successfully");

         fetchVisits();

      } catch {

         message.error("Delete failed");

      }
   };

   const resetFilters = () => {

      setSearch("");
      setStatus("");
      setDateRange([]);

   };

   const handleExport = async () => {
      try {
         await exportDailyVisits();
      } catch (err) {
         message.error(err.message);
      }
   };

   // ---------------- TABLE COLUMNS ----------------

   const columns = [
      {
         title: "Sales Person",
         dataIndex: "sales_person"
      },
      {
         title: "Client",
         dataIndex: "client_name"
      },
      {
         title: "Phone",
         dataIndex: "phone"
      },
      {
         title: "Location",
         dataIndex: "location"
      },
      {
         title: "Remarks",
         dataIndex: "note",
         width: 150,
         ellipsis: {
            showTitle: false
         },
         render: (note) => (
            <Tooltip placement="topLeft" title={note}>
               {note || "-"}
            </Tooltip>
         )
      },
      {
         title: "Visit Date",
         dataIndex: "visit_date",
         render: (date) =>
            date ? dayjs(date).format("DD/MM/YYYY") : "-"
      },
      {
         title: "Next Follow",
         dataIndex: "next_follow_date",
         render: (date) =>
            date ? dayjs(date).format("DD/MM/YYYY") : "-"
      },
      {
         title: "Status",
         dataIndex: "status",
         render: (status) => {

            const colors = {
               deal_closed: "green",
               in_process: "blue",
               cancel: "red"
            };

            return (
               <Tag color={colors[status]}>
                  {status}
               </Tag>
            );

         }
      },
      {
         title: "Created At",
         dataIndex: "created_at",
         render: (date) =>
            date ? dayjs(date).format("DD/MM/YYYY hh:mm A") : "-"
      },
      {
         title: "Action",
         width: 100,
         align: "center",
         render: (_, record) => (
            <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>

               {/* ✅ Edit Icon Button */}
               <Button
                  type="primary"
                  shape="circle"
                  icon={<EditOutlined />}
                  onClick={() => handleEdit(record)}
                  title="Edit"
               />

               {/* ✅ Delete Icon Button */}
               <Popconfirm
                  title="Delete this visit?"
                  onConfirm={() => handleDelete(record.id)}
                  okText="Yes"
                  cancelText="No"
               >
                  <Button
                     danger
                     shape="circle"
                     icon={<DeleteOutlined />}
                     title="Delete"
                  />
               </Popconfirm>

            </div>
         )
      }
   ];

   return (
      <>
         <div className="bg-white border-b border-gray-200 px-6 py-1">
            <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
               <h1 className="text-2xl font-semibold text-gray-900 m-0">Daily Visits Record</h1>
               <div>
                  <Button
                     style={{ marginRight: 10 }}
                     onClick={handleExport}
                  >
                     Export Excel
                  </Button>
                  <Button
                     type="primary"
                     icon={<PlusOutlined />}
                     onClick={() => setIsAddModalOpen(true)}
                  >
                     Add Visit
                  </Button>
               </div>
            </Row>

            {/* FILTER BAR */}

            <Row gutter={16} style={{ marginBottom: 20 }}>

               <Col span={8}>
                  <Input.Search
                     placeholder="Search by sales person client / phone"
                     allowClear
                     onSearch={(value) => setSearch(value)}
                  />
               </Col>

               <Col span={6}>
                  <Select
                     placeholder="Filter by status"
                     allowClear
                     style={{ width: "100%" }}
                     onChange={(value) => setStatus(value || "")}
                  >
                     <Select.Option value="in_process">
                        In Process
                     </Select.Option>

                     <Select.Option value="deal_closed">
                        Deal Closed
                     </Select.Option>

                     <Select.Option value="cancel">
                        Cancel
                     </Select.Option>

                  </Select>
               </Col>

               <Col span={6}>
                  <DatePicker.RangePicker
                     style={{ width: "100%" }}
                     format="DD/MM/YYYY"
                     onChange={(dates) =>
                        setDateRange(dates || [])
                     }
                  />
               </Col>

               <Col span={4}>
                  <Button block onClick={resetFilters}>
                     Reset
                  </Button>
               </Col>

            </Row>

            <Table
               columns={columns}
               dataSource={visits}
               rowKey="id"
               size="small"
               tableLayout="fixed"
               scroll={{ x: 1100 }}
            />
         </div>

         {/* EDIT MODAL */}

         <Modal
            title="Edit Daily Visit"
            open={isModalOpen}
            onOk={handleUpdate}
            onCancel={() => setIsModalOpen(false)}
            width={700}
         >

            <Form
               form={form}
               layout="vertical"
            >

               <Row gutter={16}>

                  <Col span={12}>
                     <Form.Item
                        label="Client Name"
                        name="client_name"
                        rules={[{ required: true }]}
                        style={{ marginBottom: 16 }}
                     >
                        <Input />
                     </Form.Item>
                  </Col>

                  <Col span={12}>
                     <Form.Item
                        label="Phone"
                        name="phone"
                        style={{ marginBottom: 16 }}
                     >
                        <Input />
                     </Form.Item>
                  </Col>

               </Row>

               <Row gutter={16}>

                  <Col span={12}>
                     <Form.Item
                        label="Location"
                        name="location"
                        style={{ marginBottom: 16 }}
                     >
                        <Input />
                     </Form.Item>
                  </Col>

                  <Col span={12}>
                     <Form.Item
                        label="Status"
                        name="status"
                        style={{ marginBottom: 16 }}
                     >
                        <Select>
                           <Select.Option value="in_process">
                              In Process
                           </Select.Option>
                           <Select.Option value="deal_closed">
                              Deal Closed
                           </Select.Option>
                           <Select.Option value="cancel">
                              Cancel
                           </Select.Option>
                        </Select>
                     </Form.Item>
                  </Col>

               </Row>

               <Row gutter={16}>

                  <Col span={12}>
                     <Form.Item
                        label="Visit Date"
                        name="visit_date"
                        rules={[
                           {
                              required: true,
                              message: "Please select visit date"
                           }
                        ]}
                        style={{ marginBottom: 16 }}
                     >
                        <DatePicker
                           style={{ width: "100%" }}
                           format="DD/MM/YYYY"
                        />
                     </Form.Item>
                  </Col>

                  <Col span={12}>
                     <Form.Item
                        label="Next Follow Date"
                        name="next_follow_date"
                        style={{ marginBottom: 16 }}
                     >
                        <DatePicker
                           style={{ width: "100%" }}
                           format="DD/MM/YYYY"
                        />
                     </Form.Item>
                  </Col>

               </Row>

               <Row>
                  <Col span={24}>
                     <Form.Item
                        label="Remarks"
                        name="note"
                        style={{ marginBottom: 8 }}
                     >
                        <Input.TextArea rows={2} />
                     </Form.Item>
                  </Col>
               </Row>

            </Form>

         </Modal>

         {/* ADD MODAL */}

         <Modal
            title="Add Daily Visit"
            open={isAddModalOpen}
            onCancel={() => setIsAddModalOpen(false)}
            footer={null}
            width={700}
         >

            <Form
               form={addForm}
               layout="vertical"
               onFinish={handleAddVisit}
            >

               <Row gutter={16}>

                  <Col span={12}>
                     <Form.Item
                        label="Client Name"
                        name="client_name"
                        rules={[
                           {
                              required: true,
                              message: "Please enter client name"
                           }
                        ]}
                        style={{ marginBottom: 16 }}
                     >
                        <Input />
                     </Form.Item>
                  </Col>

                  <Col span={12}>
                     <Form.Item
                        label="Phone"
                        name="phone"
                        rules={[
                           {
                              required: true,
                              message: "Please enter phone number"
                           },
                           {
                              pattern: /^[0-9]{10}$/,
                              message: "Enter valid 10 digit mobile number"
                           }
                        ]}
                        style={{ marginBottom: 16 }}
                     >
                        <Input />
                     </Form.Item>
                  </Col>

               </Row>

               <Row gutter={16}>

                  <Col span={12}>
                     <Form.Item
                        label="Location"
                        name="location"
                        rules={[
                           {
                              required: true,
                              message: "Please enter location"
                           }
                        ]}
                        style={{ marginBottom: 16 }}
                     >
                        <Input />
                     </Form.Item>
                  </Col>

                  <Col span={12}>
                     <Form.Item
                        label="Status"
                        name="status"
                        initialValue="in_process"
                        style={{ marginBottom: 16 }}
                     >
                        <Select>
                           <Select.Option value="in_process">
                              In Process
                           </Select.Option>
                           <Select.Option value="deal_closed">
                              Deal Closed
                           </Select.Option>
                           <Select.Option value="cancel">
                              Cancel
                           </Select.Option>
                        </Select>
                     </Form.Item>
                  </Col>

               </Row>

               <Row gutter={16}>

                  <Col span={12}>
                     <Form.Item
                        label="Visit Date"
                        name="visit_date"
                        rules={[
                           {
                              required: true,
                              message: "Please select visit date"
                           }
                        ]}
                        style={{ marginBottom: 16 }}
                     >
                        <DatePicker
                           style={{ width: "100%" }}
                           format="DD/MM/YYYY"
                        />
                     </Form.Item>
                  </Col>

                  <Col span={12}>
                     <Form.Item
                        label="Next Follow Date"
                        name="next_follow_date"
                        style={{ marginBottom: 16 }}
                     >
                        <DatePicker
                           style={{ width: "100%" }}
                           format="DD/MM/YYYY"
                        />
                     </Form.Item>
                  </Col>

               </Row>

               <Row>
                  <Col span={24}>
                     <Form.Item
                        label="Remarks"
                        name="note"
                        style={{ marginBottom: 16 }}
                     >
                        <Input.TextArea rows={2} />
                     </Form.Item>
                  </Col>
               </Row>

               <Button type="primary" htmlType="submit" block>
                  Save Visit
               </Button>

            </Form>

         </Modal>

      </>
   );

};

export default DailyVisits;